import * as React from "react";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { cn } from "../lib/utils";

function isStepEqual(a, b) {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  if (String(a) === String(b)) return true;
  const numA = Number(a);
  const numB = Number(b);
  if (!isNaN(numA) && !isNaN(numB) && numA === numB) return true;
  return false;
}

function isStepBefore(a, b) {
  const numA = Number(a);
  const numB = Number(b);
  if (!isNaN(numA) && !isNaN(numB)) {
    return numA < numB;
  }
  return false;
}

const StepperContext = React.createContext({
  value: 1,
  onValueChange: () => {},
  orientation: "vertical",
  indicators: {},
});

const Stepper = React.forwardRef(
  (
    {
      value: controlledValue,
      defaultValue = 1,
      onValueChange,
      orientation = "vertical",
      indicators = {},
      steps, // optional data-driven array syntax
      activeStep, // optional alias for value
      className,
      children,
      ...props
    },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = React.useState(
      activeStep !== undefined ? activeStep : defaultValue
    );

    const isControlled = controlledValue !== undefined || activeStep !== undefined;
    const value = controlledValue !== undefined ? controlledValue : (activeStep !== undefined ? activeStep : uncontrolledValue);

    const handleValueChange = React.useCallback(
      (nextStep) => {
        if (!isControlled) {
          setUncontrolledValue(nextStep);
        }
        onValueChange?.(nextStep);
      },
      [isControlled, onValueChange]
    );

    const contextValue = React.useMemo(
      () => ({
        value,
        onValueChange: handleValueChange,
        orientation,
        indicators,
      }),
      [value, handleValueChange, orientation, indicators]
    );

    // If data-driven steps array is provided without children, render automatically
    if (steps && Array.isArray(steps) && !children) {
      return (
        <StepperContext.Provider value={contextValue}>
          <div
            ref={ref}
            className={cn(
              "flex",
              orientation === "vertical" ? "flex-col" : "flex-row items-center w-full",
              className
            )}
            role="tablist"
            aria-orientation={orientation}
            {...props}
          >
            <StepperNav orientation={orientation}>
              {steps.map((st, idx) => {
                const stepNumber = idx + 1;
                const isCompleted =
                  st.status === "completed" ||
                  (st.status === undefined && isStepBefore(stepNumber, value));
                const isActive =
                  st.status === "current" ||
                  st.status === "active" ||
                  (st.status === undefined &&
                    (isStepEqual(stepNumber, value) ||
                      (st.id !== undefined && isStepEqual(st.id, value))));
                const isError = st.status === "error";
                const isLoading = st.status === "loading" || st.loading;

                return (
                  <StepperItem
                    key={st.id || idx}
                    step={stepNumber}
                    completed={isCompleted}
                    active={isActive}
                    disabled={st.disabled}
                    loading={isLoading}
                    isLast={idx === steps.length - 1}
                  >
                    <StepperTrigger step={stepNumber} disabled={st.disabled}>
                      <StepperIndicator
                        step={stepNumber}
                        completed={isCompleted}
                        active={isActive}
                        error={isError}
                        loading={isLoading}
                      >
                        {st.icon}
                      </StepperIndicator>
                      <div className="flex flex-col text-left min-w-0">
                        <StepperTitle>{st.title}</StepperTitle>
                        {(st.description || st.timestamp) && (
                          <StepperDescription>
                            {st.description}
                            {st.timestamp && (
                              <span className="ml-1 opacity-75">· {st.timestamp}</span>
                            )}
                          </StepperDescription>
                        )}
                      </div>
                    </StepperTrigger>
                    {idx < steps.length - 1 && (
                      <StepperSeparator completed={isCompleted} />
                    )}
                  </StepperItem>
                );
              })}
            </StepperNav>
          </div>
        </StepperContext.Provider>
      );
    }

    return (
      <StepperContext.Provider value={contextValue}>
        <div
          ref={ref}
          className={cn(
            "flex",
            orientation === "vertical" ? "flex-col" : "flex-row items-center w-full",
            className
          )}
          role="tablist"
          aria-orientation={orientation}
          {...props}
        >
          {children}
        </div>
      </StepperContext.Provider>
    );
  }
);
Stepper.displayName = "Stepper";

const StepperNav = React.forwardRef(({ className, children, ...props }, ref) => {
  const { orientation } = React.useContext(StepperContext);
  return (
    <div
      ref={ref}
      className={cn(
        "flex",
        orientation === "vertical" ? "flex-col" : "flex-row items-center w-full",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
});
StepperNav.displayName = "StepperNav";

const StepperItemContext = React.createContext({
  step: 1,
  completed: false,
  active: false,
  disabled: false,
  loading: false,
  error: false,
  isLast: false,
});

const StepperItem = React.forwardRef(
  (
    {
      step,
      completed: explicitCompleted,
      active: explicitActive,
      disabled = false,
      loading = false,
      error = false,
      isLast = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const { value, orientation } = React.useContext(StepperContext);

    const completed =
      explicitCompleted !== undefined ? explicitCompleted : isStepBefore(step, value);
    const active = explicitActive !== undefined ? explicitActive : isStepEqual(step, value);

    const itemContext = React.useMemo(
      () => ({
        step,
        completed,
        active,
        disabled,
        loading,
        error,
        isLast,
      }),
      [step, completed, active, disabled, loading, error, isLast]
    );

    return (
      <StepperItemContext.Provider value={itemContext}>
        <div
          ref={ref}
          data-state={active ? "active" : completed ? "completed" : "upcoming"}
          className={cn(
            "group/step relative flex",
            orientation === "vertical"
              ? "flex-col pb-6 last:pb-0"
              : "flex-1 flex-col items-center",
            className
          )}
          {...props}
        >
          {children}
        </div>
      </StepperItemContext.Provider>
    );
  }
);
StepperItem.displayName = "StepperItem";

const StepperTrigger = React.forwardRef(
  ({ step: explicitStep, className, children, onClick, ...props }, ref) => {
    const { value, onValueChange, orientation } = React.useContext(StepperContext);
    const item = React.useContext(StepperItemContext);
    const step = explicitStep !== undefined ? explicitStep : item.step;
    const isSelected = isStepEqual(value, step);

    const handleKeyDown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onValueChange(step);
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        aria-selected={isSelected}
        disabled={item.disabled}
        onClick={(e) => {
          onClick?.(e);
          if (!item.disabled) {
            onValueChange(step);
          }
        }}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex gap-3.5 rounded-xl text-left transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          orientation === "vertical"
            ? "items-start w-full"
            : "flex-col items-center text-center min-h-[44px]",
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);
StepperTrigger.displayName = "StepperTrigger";

const StepperIndicator = React.forwardRef(
  (
    {
      step: explicitStep,
      completed: explicitCompleted,
      active: explicitActive,
      error: explicitError,
      loading: explicitLoading,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const item = React.useContext(StepperItemContext);
    const step = explicitStep !== undefined ? explicitStep : item.step;
    const completed =
      explicitCompleted !== undefined ? explicitCompleted : item.completed;
    const active = explicitActive !== undefined ? explicitActive : item.active;
    const error = explicitError !== undefined ? explicitError : item.error;
    const loading = explicitLoading !== undefined ? explicitLoading : item.loading;

    return (
      <div
        ref={ref}
        className={cn(
          "relative z-10 flex h-7 w-7 size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-all duration-200",
          completed &&
            "bg-success/20 text-success border border-success/40",
          active &&
            "bg-primary text-primary-foreground font-bold ring-4 ring-primary/20 border border-primary",
          !completed &&
            !active &&
            !error &&
            "bg-muted text-muted-foreground border border-border",
          error &&
            "bg-destructive/20 text-destructive border border-destructive/40",
          className
        )}
        {...props}
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : error ? (
          <AlertCircle className="h-3.5 w-3.5" />
        ) : completed ? (
          <Check className="h-3.5 w-3.5 stroke-[2.5]" />
        ) : children ? (
          children
        ) : (
          step
        )}
      </div>
    );
  }
);
StepperIndicator.displayName = "StepperIndicator";

const StepperSeparator = React.forwardRef(
  ({ completed: explicitCompleted, className, ...props }, ref) => {
    const { orientation } = React.useContext(StepperContext);
    const item = React.useContext(StepperItemContext);
    const completed =
      explicitCompleted !== undefined ? explicitCompleted : item.completed;

    if (orientation === "horizontal") {
      return (
        <div
          ref={ref}
          className={cn(
            "h-0.5 w-full flex-1 transition-colors duration-200",
            completed ? "bg-success/50" : "bg-border",
            className
          )}
          {...props}
        />
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          "absolute left-[13px] top-7 bottom-0 w-0.5 transition-colors duration-200",
          completed ? "bg-success/50" : "bg-border",
          className
        )}
        {...props}
      />
    );
  }
);
StepperSeparator.displayName = "StepperSeparator";

const StepperTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <h4
    ref={ref}
    className={cn(
      "text-sm font-semibold tracking-tight text-foreground leading-snug pt-0.5",
      className
    )}
    {...props}
  >
    {children}
  </h4>
));
StepperTitle.displayName = "StepperTitle";

const StepperDescription = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <p
      ref={ref}
      className={cn("text-xs text-muted-foreground mt-1 leading-normal", className)}
      {...props}
    >
      {children}
    </p>
  )
);
StepperDescription.displayName = "StepperDescription";

const StepperPanel = React.forwardRef(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("mt-4 flex-1", className)} {...props}>
    {children}
  </div>
));
StepperPanel.displayName = "StepperPanel";

const StepperContent = React.forwardRef(
  ({ step, value: explicitValue, className, children, ...props }, ref) => {
    const { value } = React.useContext(StepperContext);
    const targetStep = explicitValue !== undefined ? explicitValue : step;

    if (!isStepEqual(value, targetStep)) return null;

    return (
      <div
        ref={ref}
        role="tabpanel"
        tabIndex={0}
        className={cn("focus-visible:outline-none", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
StepperContent.displayName = "StepperContent";

// Aliases for alternate naming conventions
const Step = StepperItem;
const StepIndicator = StepperIndicator;
const StepTitle = StepperTitle;
const StepDescription = StepperDescription;
const StepStatus = StepperIndicator;

export {
  Stepper,
  Step,
  StepIndicator,
  StepTitle,
  StepDescription,
  StepStatus,
  StepperItem,
  StepperTrigger,
  StepperSeparator,
  StepperContent,
  StepperNav,
  StepperPanel,
};
